import { AllocationUsageType, ExternalEventAllocationUsageType, ProjectAllocationUsageType } from '@/generated/graphql';
import { DateTime } from 'luxon';
import type { RouteLocationRaw } from 'vue-router';
import type { SpecializationType } from './allocation/SpecializationMappings';
import { AllocationTooltipProps } from './components/AllocationTooltip.vue';

// Deliberately two types with the same members: the shape of a whole allocation vs. the shape of one phase
export type AllocationForm = 'Start-End' | 'Start-Middle' | 'Middle' | 'Middle-End';
export type PhaseForm = 'Start-End' | 'Start-Middle' | 'Middle' | 'Middle-End';
export type AllocationPartForm = 'Start' | 'Middle' | 'End' | 'Single';

export type AllocationType = 'Event' | 'Project' | 'ExternalEvent';
export type AllocationTypeWithAggregation = AllocationType | 'Aggregation';

export type AllocationPhasePosition = 'Before-Main' | 'Main' | 'After-Main';

export type AllocationPhase = {
    id: string;
    cellSpan: number;
    position: AllocationPhasePosition;
    specialization: SpecializationType | null;
    color: PhaseColor;
    usage: {
        id: string;
        position: number;
        type: PhaseTypeDatabase;
    };
};

export type AllocationPhaseUpdate = AllocationPhase & {
    startDateTime: DateTime;
    endDateTime: DateTime;
    cellSpanBefore: number;
    cellSpanAfter: number;
    newSpecialization: SpecializationType | null;
};

/**
 * What a click on the allocation bar does. `No-Action` covers bars that are never clickable and states why
 * in its `tooltipText`, since the reason differs per bar. Bars needing more than a line of text supply the
 * `tooltip-text` slot instead.
 */
export type AllocationClickAction =
    | { type: 'Edit' }
    | { type: 'Jump-To-Allocation' }
    | {
          type: 'Open-Url';
          target: RouteLocationRaw;
          tooltipText: string;
      }
    | { type: 'No-Action'; tooltipText: string };

export type Allocation = {
    id: string;
    label: string;
    variantLabel?: string;
    groupLabel: string;
    phases: AllocationPhase[];
    startDateTime: DateTime;
    endDateTime: DateTime;
    locationId: string;
    clickAction: AllocationClickAction;
    linkTarget: RouteLocationRaw;
    variant: 'Solid' | 'Outer-Semi-Transparent' | 'Semi-Transparent' | 'Faint';
    labelStyle?: 'Strong' | 'Muted';
    isOptional: boolean;
    allocationType: AllocationTypeWithAggregation;
    allocationVariant: {
        id: string;
        index: number;
        label: string;
        target: RouteLocationRaw;
        referenceAllocationId: string | null;
    };
    locationTypeCategory: LocationTypeCategory;
    isInside?: boolean;
    allocationTooltip?: AllocationTooltipProps;
};

export type LocationTypeCategoryInside = 'Building' | 'Floor' | 'Room' | 'Partition';
export type LocationTypeCategoryOutside = 'Area' | 'Zone' | 'Plot';
export type LocationTypeExternalLocation = 'ExternalLocation';
export type LocationTypeCategory =
    | LocationTypeCategoryInside
    | LocationTypeCategoryOutside
    | LocationTypeExternalLocation;

const LOCATION_TYPES_ROOM = [
    'Hallenausstellungsfläche',
    'Eingang/Zugang',
    'Boulevardsektion',
    'Passage',
    'Passage inkl. Durchfahrtstor',
    'Büro/Besprecher (ZBV intern)', // ZBV = Zur besonderen Verwendung
    'Büro/Besprecher (ZBV extern)',
    'Lager (ZBV intern)',
    'Lager (ZBV extern)',
    'Konferenzraum',
    'Konferenzraum-DUMMY',
    'Service-Center',
    'Fahrstraße (Parkdeck)',
    'Rampe (Parkdeck)',
] as const;

const LOCATION_TYPES_AREA = [
    'Parkfläche (intern)',
    'Parkfläche (extern)',
    'Außenfläche (intern)',
    'Außenfläche (extern)',
    'Geländezufahrt/-zugang',
    'Fahrstraße (intern)',
    'Fahrstraße (extern)',
] as const;

// The single source of truth for both the guard below and `LocationType`, so they cannot disagree
const LOCATION_TYPES = [...LOCATION_TYPES_ROOM, ...LOCATION_TYPES_AREA] as const;

export type LocationType = (typeof LOCATION_TYPES)[number];

/**
 * The location types the timeline fetches and renders — passed to the queries as variables. Narrower
 * than `LocationType`: a type the timeline no longer shows (e.g. "Konferenzraum") stays known here so
 * icons and labels keep working for it in the location hierarchy, it is just not fetched as a row.
 */
export const TIMELINE_ROOM_TYPES: LocationType[] = [
    'Hallenausstellungsfläche',
    'Boulevardsektion',
    'Passage',
    'Passage inkl. Durchfahrtstor',
    'Eingang/Zugang',
    'Service-Center',
    'Konferenzraum-DUMMY',
    'Büro/Besprecher (ZBV intern)',
    'Fahrstraße (Parkdeck)',
    'Rampe (Parkdeck)',
];
export const TIMELINE_AREA_TYPES: LocationType[] = [...LOCATION_TYPES_AREA];

export function isLocationType(value: string | undefined): value is LocationType {
    return LOCATION_TYPES.some((type) => type === value);
}

export type ConflictStatus = 'No-Conflict' | 'Resolved-Conflict' | 'Unresolved-Conflict';
export type ConflictMarker = 'No-Conflict' | 'Conflict-Start' | 'Conflict-Middle' | 'Conflict-End' | 'Conflict-Single';

/**
 * Maps an ISO date string in the form of YYYY-MM-DD to the conflict status for this day column.
 */
export type ConflictsByDay = Map<string, ConflictStatus>;

export type Section = {
    id: string;
    label: string;
    typeCategory: LocationTypeCategory;
    type: LocationType;
    isExpanded: boolean;
};

export type AggregationPhaseTypeDatabase = 'Aggregation';

export type PhaseTypeDatabase =
    | AllocationUsageType
    | ProjectAllocationUsageType
    | ExternalEventAllocationUsageType
    | AggregationPhaseTypeDatabase;

export type PhaseColor =
    | 'Pre-External-Event-Category-Project'
    | 'External-Event-Category-Project'
    | 'Post-External-Event-Category-Project'
    | 'Pre-External-Event-Category-Event'
    | 'External-Event-Category-Event'
    | 'Post-External-Event-Category-Event'
    | 'Pre-External-Event-Category-External-Event'
    | 'External-Event-Category-External-Event'
    | 'Post-External-Event-Category-External-Event'
    | 'Technical-Set-Up'
    | 'Technical-Breakdown'
    | 'Extended-Technical-Set-Up'
    | 'Extended-Technical-Breakdown'
    | 'Early-Set-Up'
    | 'Extended-Breakdown'
    | 'Regular-Set-Up'
    | 'Regular-Breakdown'
    | 'Event' // = 'Show-Days' in usage
    | 'Project-Set-Up'
    | 'Project-Breakdown'
    | 'Project'
    | AggregationPhaseTypeDatabase;
